import {
    createEntityAdapter,
    createSlice as toolkitCreateSlice,
    EntityState,
    nanoid,
    SliceCaseReducers,
    ValidateSliceCaseReducers
} from '@reduxjs/toolkit';

export interface ToastMessage<T> {
    id: string;
    createdAt: number;
    timeInMillis?: number;
    data: T;
}

export type ToastState<T> = EntityState<ToastMessage<T>>;

export const createToastComponents = <T>() => {
    // entity adapter to track toast message state
    const adapter = createEntityAdapter<ToastMessage<T>>({
        sortComparer: (prev, next) => {
            if (prev.createdAt < next.createdAt) {
                return 1;
            }

            if (prev.createdAt > next.createdAt) {
                return -1;
            }

            return 0;
        }
    });

    const createSlice = <Reducers extends SliceCaseReducers<ToastState<T>>>({
        name = 'toast',
        reducers = {} as Reducers & ValidateSliceCaseReducers<ToastState<T>, Reducers>
    }: {
        name: string;
        reducers?: Reducers & ValidateSliceCaseReducers<ToastState<T>, Reducers>;
    }) => {
        return toolkitCreateSlice({
            name,
            initialState: adapter.getInitialState(),
            reducers: {
                ...reducers,
                showMessage: {
                    reducer: adapter.addOne,
                    prepare: (params: Omit<ToastMessage<T>, 'id' | 'createdAt'>) => {
                        return {
                            payload: {
                                ...params,
                                id: nanoid(),
                                createdAt: Date.now(),
                                timeInMillis: params.timeInMillis || 7000
                            }
                        };
                    }
                },
                clearMessage: adapter.removeOne
            }
        });
    };

    return {
        adapter,
        createSlice
    };
};
